import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  CreateCondominioRequestSchema,
  type CreateCondominioRequest,
} from '@condo/contracts';
import { isValidRut } from '@condo/domain-chile';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { RutInput } from '@/components/common/rut-input';
import { useCreateCondominio } from '@/features/condominios/api';

// Narrow form schema: omit optional featureFlags (not shown in v1 UI)
// and tighten RUT validation with the modulo-11 check.
const FormSchema = CreateCondominioRequestSchema.omit({ featureFlags: true }).extend({
  rut: z.string().refine(isValidRut, { message: 'RUT inválido (dv no coincide).' }),
});

type FormInput = z.input<typeof FormSchema>;
type FormOutput = z.output<typeof FormSchema>;

export function NuevoCondominioRoute() {
  const navigate = useNavigate();
  const mutation = useCreateCondominio();
  const form = useForm<FormInput, unknown, FormOutput>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      rut: '',
      nombre: '',
      direccion: { calle: '', numero: '', comuna: '', region: '' },
    },
  });

  const onSubmit = async (values: CreateCondominioRequest) => {
    try {
      const created = await mutation.mutateAsync(values);
      toast.success('Condominio creado', {
        description: 'Ajusta los modulos si es necesario en la pestana Feature flags.',
      });
      navigate(`/app/condominios/${created.id}`);
    } catch (err) {
      toast.error('No se pudo crear el condominio');
      console.error(err);
    }
  };

  return (
    <PageContainer width="form">
      <PageHeader
        eyebrow="Panel super admin"
        title="Crear nuevo condominio"
        subtitle="Se creara con la configuracion de modulos por defecto."
      />
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <Label htmlFor="rut">RUT</Label>
          <RutInput
            id="rut"
            className="mt-1.5"
            value={form.watch('rut')}
            onChange={(v) => form.setValue('rut', v, { shouldValidate: true })}
          />
          {form.formState.errors.rut && (
            <p className="mt-1 text-xs text-[var(--color-danger)]">
              {form.formState.errors.rut.message}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="nombre">Nombre</Label>
          <Input id="nombre" className="mt-1.5" {...form.register('nombre')} />
          {form.formState.errors.nombre && (
            <p className="mt-1 text-xs text-[var(--color-danger)]">
              {form.formState.errors.nombre.message}
            </p>
          )}
        </div>

        <fieldset className="space-y-4">
          <legend className="eyebrow mb-2 text-sm font-semibold">Direccion</legend>
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <Label htmlFor="calle">Calle</Label>
              <Input id="calle" className="mt-1.5" {...form.register('direccion.calle')} />
              {form.formState.errors.direccion?.calle && (
                <p className="mt-1 text-xs text-[var(--color-danger)]">
                  {form.formState.errors.direccion.calle.message}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="numero">Número</Label>
              <Input id="numero" className="mt-1.5" {...form.register('direccion.numero')} />
              {form.formState.errors.direccion?.numero && (
                <p className="mt-1 text-xs text-[var(--color-danger)]">
                  {form.formState.errors.direccion.numero.message}
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="comuna">Comuna</Label>
              <Input id="comuna" className="mt-1.5" {...form.register('direccion.comuna')} />
              {form.formState.errors.direccion?.comuna && (
                <p className="mt-1 text-xs text-[var(--color-danger)]">
                  {form.formState.errors.direccion.comuna.message}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="region">Región</Label>
              <Input id="region" className="mt-1.5" {...form.register('direccion.region')} />
              {form.formState.errors.direccion?.region && (
                <p className="mt-1 text-xs text-[var(--color-danger)]">
                  {form.formState.errors.direccion.region.message}
                </p>
              )}
            </div>
          </div>
        </fieldset>

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            type="button"
            onClick={() => navigate('/app/platform')}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Crear condominio
          </Button>
        </div>
      </form>
    </PageContainer>
  );
}
